import { useQuery } from "react-query";
import { useState } from "react";
import {
  getPatient,
  getDispositions,
  getEDDs,
  getPatientDispositions,
  getConfiguration,
  getIntelligenceText,
  getMilestones,
  getPatientMilestones,
  getPatientInsights,
  getMedicallyReadyStatus,
  getActiveRoundingSession,
  getPatientConsiderations,
  getPatientList,
  getDepartments,
  getDepartmentGroups,
  getPatientListFilters,
  getPatientNotes,
  getUserPreferences,
  listEDDs,
  listPatientDispositions,
  listPatientMilestones,
  listPatientClinicalInsights,
  listIntelligenceText,
  listMedicallyReadyStatus,
  listPatientInsights,
  listPatientNotes,
  getPatientClinicalInsights,
} from "./endpoints";
import type { UseQueryResult } from "react-query";
import type Patient from "../types/Patient";
import type Disposition from "../types/Disposition";
import type PatientDisposition from "../types/PatientDisposition";
import type EDD from "../types/EDD";
import type Configuration from "../types/Configuration";
import type IntelligenceText from "../types/IntelligenceText";
import PatientMilestone from "../types/PatientMilestone";
import PatientClinicalInsight from "../types/PatientClinicalInsight";
import PatientInsights from "../types/PatientInsights";
import Milestone from "../types/Milestone";
import MedicallyReadyStatus from "../types/MedicallyReadyStatus";
import RoundingSession from "../types/RoundingSession";
import PatientConsideration from "../types/PatientConsideration";
import Department from "../types/Department";
import DepartmentGroup from "../types/DepartmentGroup";
import PatientListEntry from "../types/PatientListEntry";
import PatientNote from "../types/PatientNote";
import ListFilter from "../types/ListFilter";
import UserPreferences from "../types/UserPreferences";

const usePatientQuery = (
  emrPatientID: string,
  emrHospitalID: string,
  fhirPatientID: string
): UseQueryResult<Patient> => {
  return useQuery<Patient, Error>(
    ["patient", emrPatientID, emrHospitalID, fhirPatientID],
    () => getPatient({ emrPatientID, emrHospitalID, fhirPatientID })
  );
};

const useEDDQuery = (
  patientID: number,
  includeHistory = false
): UseQueryResult<EDD[]> =>
  useQuery<EDD[], Error>(
    ["edd", patientID, includeHistory],
    () => getEDDs(patientID, includeHistory),
    {
      enabled: !!patientID,
      staleTime: 0,
    }
  );

const useListEDDQuery = (
  patientIDs: number[]
): UseQueryResult<Record<number, EDD[]>> =>
  useQuery<Record<number, EDD[]>, Error>(
    ["eddlist", patientIDs],
    () => listEDDs(patientIDs),
    {
      enabled: patientIDs.length > 0,
      staleTime: 0,
    }
  );

const useDispositionsQuery = (
  isEnabled = true
): UseQueryResult<Disposition[]> =>
  useQuery<Disposition[], Error>("dispositions", getDispositions, {
    enabled: isEnabled,
  });

const usePatientDispositionsQuery = (
  patientID: number,
  includeHistory = false
): UseQueryResult<PatientDisposition[]> => {
  const [shouldStopRefetch, setShouldStopRefetch] = useState(false);

  return useQuery<PatientDisposition[], Error>(
    ["patientdispositions", patientID, includeHistory],
    () => getPatientDispositions(patientID, includeHistory),
    {
      enabled: !!patientID,
      refetchInterval: shouldStopRefetch ? false : 5000,
      staleTime: 0,
      onError: (error: any) => {
        // Check if it's a 403 error with JWT token expired message
        if (
          error?.response?.status === 403 &&
          error?.response?.data?.detail?.includes("JWT token has expired")
        ) {
          setShouldStopRefetch(true);
        }
      },
    }
  );
};

const useListPatientDispositionsQuery = (
  patientIDs: number[]
): UseQueryResult<Record<number, PatientDisposition[]>> =>
  useQuery<Record<number, PatientDisposition[]>, Error>(
    ["patientdispositionslist", patientIDs],
    () => listPatientDispositions(patientIDs),
    {
      enabled: patientIDs.length > 0,
      staleTime: 0,
    }
  );

const useMilestonesQuery = (isEnabled = true): UseQueryResult<Milestone[]> =>
  useQuery<Milestone[], Error>("milestones", getMilestones, {
    enabled: isEnabled,
  });

const usePatientMilestonesQuery = (
  patientID: number
): UseQueryResult<PatientMilestone[]> =>
  useQuery<PatientMilestone[], Error>(
    ["patientmilestones", patientID],
    () => getPatientMilestones(patientID),
    {
      enabled: !!patientID,
      staleTime: 0,
    }
  );

const useListPatientMilestonesQuery = (
  patientIDs: number[]
): UseQueryResult<Record<number, PatientMilestone[]>> =>
  useQuery<Record<number, PatientMilestone[]>, Error>(
    ["patientmilestoneslist", patientIDs],
    () => listPatientMilestones(patientIDs),
    {
      enabled: patientIDs.length > 0,
      staleTime: 0,
    }
  );

const usePatientClinicalInsightsQuery = (
  patientID: number
): UseQueryResult<PatientClinicalInsight[], Error> =>
  useQuery<PatientClinicalInsight[], Error>(
    ["patientclinicalinsights", patientID],
    () => getPatientClinicalInsights(patientID),
    {
      enabled: !!patientID,
      staleTime: 0,
    }
  );

const useListPatientClinicalInsightsQuery = (
  patientIDs: number[]
): UseQueryResult<Record<number, PatientClinicalInsight[]>, Error> =>
  useQuery<Record<number, PatientClinicalInsight[]>, Error>(
    ["patientclinicalinsightslist", patientIDs],
    () => listPatientClinicalInsights(patientIDs),
    {
      enabled: patientIDs.length > 0,
      staleTime: 0,
    }
  );

const useConfigurationQuery = (
  isEnabled = true
): UseQueryResult<Configuration[], Error> => {
  return useQuery<Configuration[], Error>(["configuration"], getConfiguration, {
    enabled: isEnabled,
  });
};

const useIntelligenceTextQuery = (
  patientID: number
): UseQueryResult<IntelligenceText[], Error> => {
  return useQuery<IntelligenceText[], Error>(
    ["intelligence", patientID],
    () => getIntelligenceText(patientID),
    { enabled: !!patientID, staleTime: 0 }
  );
};

const useListIntelligenceTextQuery = (
  patientIDs: number[]
): UseQueryResult<Record<number, IntelligenceText[]>> =>
  useQuery<Record<number, IntelligenceText[]>, Error>(
    ["intelligencelist", patientIDs],
    () => listIntelligenceText(patientIDs),
    {
      enabled: patientIDs.length > 0,
      staleTime: 0,
    }
  );

const useMedicallyReadyStatusQuery = (
  patientID: number
): UseQueryResult<MedicallyReadyStatus, Error> => {
  return useQuery<MedicallyReadyStatus, Error>(
    ["medicallyready", patientID],
    () => getMedicallyReadyStatus(patientID),
    { enabled: !!patientID, staleTime: 0 }
  );
};

const useListMedicallyReadyStatusQuery = (
  patientIDs: number[]
): UseQueryResult<Record<number, MedicallyReadyStatus>> =>
  useQuery<Record<number, MedicallyReadyStatus>, Error>(
    ["medicallyreadylist", patientIDs],
    () => listMedicallyReadyStatus(patientIDs),
    {
      enabled: patientIDs.length > 0,
      staleTime: 0,
    }
  );

const usePatientInsightsQuery = (
  patientID: number
): UseQueryResult<PatientInsights, Error> => {
  return useQuery<PatientInsights, Error>(
    ["patientinsights", patientID],
    () => getPatientInsights(patientID),
    { enabled: !!patientID, staleTime: 0 }
  );
};

const useListPatientInsightsQuery = (
  patientIDs: number[]
): UseQueryResult<Record<number, PatientInsights>> =>
  useQuery<Record<number, PatientInsights>, Error>(
    ["patientinsightslist", patientIDs],
    () => listPatientInsights(patientIDs),
    {
      enabled: patientIDs.length > 0,
      staleTime: 0,
    }
  );

const useActiveRoundingSessionQuery = (): UseQueryResult<
  RoundingSession,
  Error
> => {
  return useQuery<RoundingSession, Error>("roundingsession", () =>
    getActiveRoundingSession()
  );
};

const usePatientConsiderationsQuery = (
  patientID: number
): UseQueryResult<PatientConsideration[], Error> => {
  return useQuery<PatientConsideration[], Error>(
    ["patientconsiderations", patientID],
    () => getPatientConsiderations(patientID),
    // Considerations is qcard-only and never seeded from the list, so we want a
    // fresh fetch every time qcard opens — overrides the global refetchOnMount: false.
    { enabled: !!patientID, refetchOnMount: "always" }
  );
};

const useDepartmentsQuery = (): UseQueryResult<Department[], Error> => {
  return useQuery<Department[], Error>("departments", () => getDepartments(), {
    refetchInterval: 60000,
    staleTime: 0,
  });
};

const useDepartmentGroupsQuery = (): UseQueryResult<
  DepartmentGroup[],
  Error
> => {
  return useQuery<DepartmentGroup[], Error>(
    "departmentgroups",
    () => getDepartmentGroups(),
    { refetchInterval: 60000, staleTime: 0 }
  );
};

const usePatientListQuery = (
  department?: string,
  group?: number,
  hospitalID?: number
): UseQueryResult<PatientListEntry[], Error> => {
  return useQuery<PatientListEntry[], Error>(
    ["patientlist", department, group],
    () => getPatientList(department, group, hospitalID),
    {
      enabled: !!(department || group),
      refetchInterval: 60000,
      staleTime: 0,
    }
  );
};

const usePatientNotesQuery = (
  patientID: number
): UseQueryResult<PatientNote[], Error> => {
  return useQuery<PatientNote[], Error>(
    ["notes", patientID],
    () => getPatientNotes(patientID),
    {
      enabled: !!patientID,
      staleTime: 0,
    }
  );
};

const useListPatientNotesQuery = (
  patientIDs: number[]
): UseQueryResult<Record<number, PatientNote[]>> =>
  useQuery<Record<number, PatientNote[]>, Error>(
    ["noteslist", patientIDs],
    () => listPatientNotes(patientIDs),
    {
      enabled: patientIDs.length > 0,
      staleTime: 0,
    }
  );

const usePatientListFiltersQuery = (
  department?: string,
  group?: number,
  hospitalID?: number
): UseQueryResult<ListFilter[], Error> => {
  return useQuery<ListFilter[], Error>(
    ["patientlistfilters", department, group],
    () => getPatientListFilters(department, group, hospitalID),
    {
      enabled: !!(department || group),
      refetchInterval: 60000,
      staleTime: 0,
    }
  );
};

const useUserPreferencesQuery = (): UseQueryResult<UserPreferences, Error> => {
  return useQuery<UserPreferences, Error>("userpreferences", () =>
    getUserPreferences()
  );
};

export {
  usePatientQuery,
  useDispositionsQuery,
  usePatientDispositionsQuery,
  useListPatientDispositionsQuery,
  useEDDQuery,
  useListEDDQuery,
  useMilestonesQuery,
  usePatientMilestonesQuery,
  useListPatientMilestonesQuery,
  usePatientClinicalInsightsQuery,
  useListPatientClinicalInsightsQuery,
  useConfigurationQuery,
  useIntelligenceTextQuery,
  useListIntelligenceTextQuery,
  useMedicallyReadyStatusQuery,
  useListMedicallyReadyStatusQuery,
  usePatientInsightsQuery,
  useListPatientInsightsQuery,
  useActiveRoundingSessionQuery,
  usePatientConsiderationsQuery,
  useDepartmentsQuery,
  useDepartmentGroupsQuery,
  usePatientListQuery,
  usePatientListFiltersQuery,
  usePatientNotesQuery,
  useListPatientNotesQuery,
  useUserPreferencesQuery,
};
